import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  Bookmark,
  Check,
  Clapperboard,
  Eye,
  Heart,
  Image,
  LoaderCircle,
  MessageCircle,
  Play,
  Repeat,
  Settings,
  ShieldCheck,
  Users,
  Video,
  X
} from 'lucide-react';
import type { ReactNode } from 'react';
import { AppUser, MediaHistoryItem, UserProfile } from '../../types';
import { MediaHistorySearchBar } from '../shared/components/MediaHistorySearchBar';
import { ContentActionsMenu } from '../shared/components/ContentActionsMenu';
import {
  extractUniqueTags,
  formatCount,
  getFollowListPage,
  hasMediaLike,
  followUser,
  subscribeToFollowStatus,
  toggleMediaLike,
  unfollowUser,
  type FollowListType,
  type SubscriberListItem,
  uploadProfileAvatar
} from '../../backend/mediaService';
import { compressMedia } from '../../backend/compressionService';
import { listCreators } from '../../backend/searchService';
import {
  claimUsername,
  checkUsernameAvailability,
  normalizeUsername,
  syncUserProfileToFirestore
} from '../../backend/authService';
import { hasPolicyConsentRecord } from '../../backend/policyService';
import { blockUser, subscribeToBlockedUsers, unblockUser } from '../../backend/moderationService';
import { auth, db } from '../../backend/firebase';
import { updateProfile, deleteUser } from 'firebase/auth';
import { collection, doc, getDocs, limit, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { useLiveProfile } from '../../hooks/useLiveProfile';
import { useUserMedia } from '../../hooks/useUserMedia';
import { useReports } from '../../hooks/useReports';
import { ProfileHeader } from './ProfileHeader';
import { ProfileTabs, ProfileContentTab } from './ProfileTabs';
import { FollowButton } from '../shared/components/FollowButton';
import { ReportsTab } from './ReportsTab';
import { PostCommentsSection } from '../posts/PostCommentsSection';
import { ProfileSkeleton } from '../shared/components/Skeleton';
import { formatFollowDuration } from '../../utils/formatFollowDuration';
import { fallbackUsername } from '../../utils/username';
import type { QueryDocumentSnapshot } from 'firebase/firestore';

interface ProfileTabProps {
  profile: UserProfile;
  onUpdateProfile: (updated: UserProfile) => void;
  mediaHistory: MediaHistoryItem[];
  onDeleteHistoryItem: (id: string) => void;
  currentUser?: AppUser | null;
  onLogout?: () => void;
  onOpenAuth?: () => void;
  viewingUserId?: string;
  onOpenSubscribeModal?: (name: string, uid?: string) => void;
  onOpenAdmin?: () => void;
  onOpenProfile?: (uid: string) => void;
  onOpenUpload?: () => void;
  onOpenFeedback?: () => void;
  onOpenAppSupport?: () => void;
}

function ProfileMediaTile({
  item,
  isVideo,
  onClick,
  children
}: {
  item: MediaHistoryItem;
  isVideo: boolean;
  onClick: () => void;
  children: ReactNode;
  key?: string;
}) {
  const [videoError, setVideoError] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  const thumbnail = item.thumbnailUrl || (!isVideo ? item.mediaUrl : '');
  return (
    <div onClick={onClick} className="group relative aspect-[9/16] cursor-pointer overflow-hidden rounded-xl bg-gradient-to-br from-slate-200 via-blue-100 to-indigo-200">
      {isVideo && item.thumbnailUrl && !thumbnailError && !videoError
        ? <>
          <video
            src={item.mediaUrl}
            poster={item.thumbnailUrl}
            preload="metadata"
            playsInline
            muted
            onError={() => setVideoError(true)}
            className="h-full w-full object-cover"
          />
          <img src={item.thumbnailUrl} alt="" aria-hidden="true" className="hidden" onError={() => setThumbnailError(true)} />
        </>
        : isVideo && item.thumbnailUrl && !thumbnailError
          ? <img src={item.thumbnailUrl} alt={item.title || 'Video'} loading="lazy" onError={() => setThumbnailError(true)} className="h-full w-full object-cover" />
          : !isVideo && thumbnail && !thumbnailError
            ? <img src={thumbnail} alt={item.title || 'Post'} loading="lazy" onError={() => setThumbnailError(true)} className="h-full w-full object-cover" />
            : <div className="grid h-full w-full place-items-center text-white">{isVideo ? <Play className="h-8 w-8 fill-white/30 drop-shadow" /> : <Image className="h-8 w-8 text-white/75" />}</div>}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/25 opacity-70 transition-opacity group-hover:opacity-100" />
      {children}
      <div className="pointer-events-none absolute inset-x-2 bottom-2 flex items-center justify-between gap-1 text-[10px] font-bold text-white drop-shadow">
        <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" />{formatCount(item.views || 0)}</span>
        <span className="inline-flex items-center gap-1"><Heart className="h-3.5 w-3.5" />{formatCount(item.likes || 0)}</span>
      </div>
    </div>
  );
}

function ProfileLongThumbnail({ item }: { item: MediaHistoryItem }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="relative aspect-video w-36 shrink-0 overflow-hidden rounded-xl bg-gradient-to-br from-slate-200 to-blue-100 sm:w-52">
      {item.thumbnailUrl && !failed
        ? <img src={item.thumbnailUrl} alt="" onError={() => setFailed(true)} className="h-full w-full object-cover" />
        : <Play className="absolute inset-0 m-auto h-7 w-7 text-white" />}
      {item.duration && <span className="absolute bottom-1 right-1 rounded bg-black/75 px-1.5 py-0.5 text-[10px] font-bold text-white">{item.duration}</span>}
    </div>
  );
}

function finiteCount(value: unknown): number {
  const count = Number(value);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

export function ProfileTab({
  profile: ownProfile,
  onUpdateProfile,
  mediaHistory: _mediaHistory,
  onDeleteHistoryItem,
  currentUser,
  onLogout,
  onOpenAuth,
  viewingUserId,
  onOpenProfile,
  onOpenUpload,
  onOpenFeedback,
  onOpenAppSupport
}: ProfileTabProps) {
  const targetUid = viewingUserId || currentUser?.uid;
  const isSelf = !viewingUserId || viewingUserId === currentUser?.uid;
  const { profile: liveProfile, loading: profileLoading, error: profileError } = useLiveProfile(targetUid, isSelf);
  const [isOfficial, setIsOfficial] = useState(false);
  const mediaState = useUserMedia(targetUid);
  const reportState = useReports(isSelf ? targetUid : undefined);
  const currentMediaHistory = mediaState.items;
  const fallbackProfile = targetUid ? null : ownProfile;
  const [isFollowing, setIsFollowing] = useState(false);
  const followRequestInFlight = useRef(false);
  const [showCreators, setShowCreators] = useState(false);
  const [creators, setCreators] = useState<AppUser[]>([]);
  const [blockedUserIds, setBlockedUserIds] = useState<Set<string>>(new Set());
  const [blockedUsersReady, setBlockedUsersReady] = useState(!currentUser?.uid);
  const [blockedUsersError, setBlockedUsersError] = useState('');
  const [showBlockedUsers, setShowBlockedUsers] = useState(false);
  const [blockActionError, setBlockActionError] = useState('');
  const [showSubscribersModal, setShowSubscribersModal] = useState(false);
  const [followListType, setFollowListType] = useState<FollowListType>('followers');
  const [subscribersList, setSubscribersList] = useState<SubscriberListItem[]>([]);
  const [followListCursor, setFollowListCursor] = useState<QueryDocumentSnapshot | null>(null);
  const [hasMoreFollowList, setHasMoreFollowList] = useState(false);
  const [loadingSubscribers, setLoadingSubscribers] = useState(false);
  const [followListError, setFollowListError] = useState('');
  const [followingCreatedAt, setFollowingCreatedAt] = useState<unknown>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('all');
  const [selectedType, setSelectedType] = useState<'all' | 'video' | 'image' | 'short'>('all');
  const [contentTab, setContentTab] = useState<ProfileContentTab>('minis');
  const [selectedMediaItem, setSelectedMediaItem] = useState<MediaHistoryItem | null>(null);
  const [selectedLiked, setSelectedLiked] = useState(false);
  const [likeBusy, setLikeBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [isSavedActive, setIsSavedActive] = useState(false);
  const [settingsSavedToast, setSettingsSavedToast] = useState(false);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [name, setName] = useState(ownProfile.name);
  const [username, setUsername] = useState(ownProfile.username);
  const [email, setEmail] = useState(ownProfile.email);
  const [phone, setPhone] = useState(ownProfile.phone);
  const [address, setAddress] = useState(ownProfile.address);
  const [avatar, setAvatar] = useState(ownProfile.avatar);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const displayName = String(liveProfile?.displayName || liveProfile?.name || fallbackProfile?.name || '');
  const displayUsername = String(liveProfile?.username || fallbackProfile?.username || fallbackUsername(targetUid || ''));
  const displayAvatar = String(liveProfile?.photoURL || liveProfile?.avatar || fallbackProfile?.avatar || '');
  const displayBio = String(liveProfile?.bio || fallbackProfile?.bio || '');
  const displayEmail = isSelf ? String(liveProfile?.email || fallbackProfile?.email || '') : '';
  const profileIsMarkedOfficial = liveProfile?.isOfficial === true || liveProfile?.role === 'official';
  const location = [
    liveProfile?.city,
    liveProfile?.state,
    liveProfile?.country
  ].map(value => typeof value === 'string' ? value.trim() : '').filter(Boolean).join(', ') || (isSelf ? String(liveProfile?.address || fallbackProfile?.address || '') : '');
  const followerCount = finiteCount(liveProfile?.followerCount);
  const followingCount = finiteCount(liveProfile?.followingCount);
  const totalViews = currentMediaHistory.reduce((sum, item) => sum + finiteCount(item.views), 0);
  const totalLikes = currentMediaHistory.reduce((sum, item) => sum + finiteCount(item.likes), 0);
  const isTargetBlocked = Boolean(viewingUserId && blockedUserIds.has(viewingUserId));
  const profileShareUrl = `https://www.yetrykar.com/@${displayUsername}`;
  const availableTags = useMemo(() => extractUniqueTags(currentMediaHistory), [currentMediaHistory]);

  useEffect(() => {
    let active = true;
    setIsOfficial(profileIsMarkedOfficial);
    if (profileIsMarkedOfficial || !targetUid || !auth.currentUser) {
      return () => {
        active = false;
      };
    }

    void getDocs(query(
      collection(db, 'officialAccounts'),
      where('uid', '==', targetUid),
      limit(1)
    )).then(snapshot => {
        if (active) setIsOfficial(!snapshot.empty);
    }).catch(error => {
      console.error('Could not verify official profile status:', error);
    });

    return () => {
      active = false;
    };
  }, [targetUid, profileIsMarkedOfficial, currentUser?.uid]);

  useEffect(() => {
    setName(displayName);
    setUsername(displayUsername);
    setEmail(displayEmail);
    setPhone(String(liveProfile?.phone || fallbackProfile?.phone || ''));
    setAddress(location);
    setAvatar(displayAvatar);
  }, [displayName, displayUsername, displayEmail, displayAvatar, liveProfile?.phone, location, fallbackProfile?.phone]);

  useEffect(() => {
    if (!targetUid || isSelf || !currentUser?.uid) {
      setIsFollowing(false);
      return;
    }
    return subscribeToFollowStatus(currentUser.uid, targetUid, (following, createdAt) => {
      if (followRequestInFlight.current) return;
      setIsFollowing(following);
      setFollowingCreatedAt(following ? createdAt ?? null : null);
    });
  }, [currentUser?.uid, isSelf, targetUid]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setBlockedUserIds(new Set());
      setBlockedUsersReady(true);
      return;
    }
    setBlockedUsersReady(false);
    setBlockedUsersError('');
    return subscribeToBlockedUsers(currentUser.uid, blocked => {
      setBlockedUserIds(blocked);
      setBlockedUsersReady(true);
    }, error => {
      setBlockedUsersError(error.message);
      setBlockedUsersReady(true);
    });
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!selectedMediaItem || !currentUser?.uid) {
      setSelectedLiked(false);
      return;
    }
    let active = true;
    void hasMediaLike(selectedMediaItem.id, currentUser.uid).then(liked => {
      if (active) setSelectedLiked(liked);
    }).catch(error => console.error('Could not load media like state:', error));
    return () => { active = false; };
  }, [currentUser?.uid, selectedMediaItem?.id]);

  const openFollowList = async (type: FollowListType) => {
    setShowSubscribersModal(true);
    setFollowListType(type);
    setSubscribersList([]);
    setFollowListCursor(null);
    setHasMoreFollowList(false);
    setFollowListError('');
    setLoadingSubscribers(true);
    try {
      const uid = targetUid;
      const result = uid
        ? await getFollowListPage(uid, type, null, 20)
        : { items: [], cursor: null, hasMore: false };
      setSubscribersList(result.items);
      setFollowListCursor(result.cursor);
      setHasMoreFollowList(result.hasMore);
    } catch (error) {
      setFollowListError(error instanceof Error ? error.message : `Could not load ${type}.`);
    } finally {
      setLoadingSubscribers(false);
    }
  };

  const loadMoreFollowList = async () => {
    if (!targetUid || !followListCursor || loadingSubscribers || !hasMoreFollowList) return;
    setLoadingSubscribers(true);
    try {
      const result = await getFollowListPage(targetUid, followListType, followListCursor, 20);
      setSubscribersList(previous => [...previous, ...result.items]);
      setFollowListCursor(result.cursor);
      setHasMoreFollowList(result.hasMore);
      setFollowListError('');
    } catch (error) {
      setFollowListError(error instanceof Error ? error.message : `Could not load more ${followListType}.`);
    } finally {
      setLoadingSubscribers(false);
    }
  };

  const handleProfileFollow = async () => {
    if (!currentUser) {
      onOpenAuth?.();
      return;
    }
    if (isSelf || !targetUid) return;
    const previousCreatedAt = followingCreatedAt;
    followRequestInFlight.current = true;
    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);
    setFollowingCreatedAt(wasFollowing ? null : new Date());
    try {
      setBlockActionError('');
      if (wasFollowing) {
        await unfollowUser(targetUid, currentUser);
      } else {
        await followUser(targetUid, currentUser, displayName);
      }
    } catch (error) {
      console.error('Could not update profile follow state:', error);
      setIsFollowing(wasFollowing);
      setFollowingCreatedAt(previousCreatedAt);
      setBlockActionError(error instanceof Error ? error.message : 'Could not follow this account.');
    } finally {
      followRequestInFlight.current = false;
    }
  };

  const toggleTargetBlock = async () => {
    if (!currentUser || !viewingUserId) {
      onOpenAuth?.();
      return;
    }
    try {
      setBlockActionError('');
      if (isTargetBlocked) await unblockUser(currentUser.uid, viewingUserId);
      else await blockUser(currentUser.uid, viewingUserId);
    } catch (error) {
      setBlockActionError(error instanceof Error ? error.message : 'Could not update blocked users.');
    }
  };

  const handleShare = () => {
    void navigator.clipboard?.writeText(profileShareUrl);
    setCopiedLink(true);
    window.setTimeout(() => setCopiedLink(false), 2500);
  };

  const handleAvatarSelection = async (file: File) => {
    if (!currentUser || !isSelf) return;
    if (!hasPolicyConsentRecord(currentUser)) {
      setAvatarError('Please accept the latest YeTryKar Policies before changing your profile photo.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setAvatarBusy(true);
    setAvatarError('');
    try {
      const compressed = await compressMedia(file, 'image');
      if (compressed.size >= 3 * 1024 * 1024) {
        throw new Error('Choose an image that can be compressed below 3 MB.');
      }
      const photoURL = await uploadProfileAvatar(currentUser.uid, compressed);
      await syncUserProfileToFirestore({
        ...currentUser,
        photoURL,
        profilePhotoURL: photoURL
      }, true);
      setAvatar(photoURL);
      onUpdateProfile({ ...ownProfile, avatar: photoURL });
      if (auth.currentUser) {
        try {
          await updateProfile(auth.currentUser, { photoURL });
        } catch (error) {
          console.error('Firestore profile photo was saved, but Firebase Auth could not be updated:', error);
          setAvatarError('Photo saved to Firebase, but your sign-in profile could not be refreshed. It will use the saved photo on next login.');
        }
      }
    } catch (error) {
      setAvatarError(error instanceof Error ? error.message : 'Could not update your profile photo.');
    } finally {
      setAvatarBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSaveSettings = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!currentUser) return;
    if (!hasPolicyConsentRecord(currentUser)) {
      setUsernameError('Please review and accept the latest YeTryKar Policies before saving changes.');
      return;
    }
    setIsSavingSettings(true);
    setUsernameError(null);
    try {
      const normalizedUsername = normalizeUsername(username);
      if (!(await checkUsernameAvailability(normalizedUsername, currentUser.uid))) {
        setUsernameError('That username is already taken or invalid.');
        return;
      }
      await claimUsername(normalizedUsername, currentUser.uid);
      const updatedProfile = { ...ownProfile, name, username: normalizedUsername, email, phone, address, avatar };
      onUpdateProfile(updatedProfile);
      await syncUserProfileToFirestore({
        ...currentUser,
        displayName: name,
        username: normalizedUsername,
        email,
        phone,
        photoURL: avatar,
        address
      });
      setSettingsSavedToast(true);
      window.setTimeout(() => {
        setSettingsSavedToast(false);
        setShowSettings(false);
      }, 1200);
    } catch (error) {
      setUsernameError(error instanceof Error ? error.message : 'Unable to save profile settings.');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleMediaLike = async () => {
    if (!selectedMediaItem) return;
    if (!currentUser) {
      onOpenAuth?.();
      return;
    }
    if (likeBusy) return;
    setLikeBusy(true);
    const previous = selectedLiked;
    setSelectedLiked(!previous);
    setSelectedMediaItem(item => item ? { ...item, likes: Math.max(0, (item.likes || 0) + (previous ? -1 : 1)) } : item);
    try {
      const result = await toggleMediaLike(selectedMediaItem.id, currentUser.uid);
      setSelectedLiked(result);
    } catch (error) {
      setSelectedLiked(previous);
      setSelectedMediaItem(item => item ? { ...item, likes: Math.max(0, (item.likes || 0) + (previous ? 1 : -1)) } : item);
      setBlockActionError(error instanceof Error ? error.message : 'Could not update like.');
    } finally {
      setLikeBusy(false);
    }
  };

  const handleRepost = async (item: MediaHistoryItem) => {
    if (!currentUser) {
      onOpenAuth?.();
      return;
    }
    try {
      const repostRef = doc(collection(db, 'reposts'));
      await setDoc(repostRef, {
        id: repostRef.id,
        originalPostId: item.id,
        originalOwnerId: item.userId || targetUid,
        reposterId: currentUser.uid,
        originalChannel: item.title || 'creator',
        timestamp: serverTimestamp(),
        createdAt: serverTimestamp()
      });
    } catch (error) {
      setBlockActionError(error instanceof Error ? error.message : 'Could not repost this content.');
    }
  };

  const normalizedType = (item: MediaHistoryItem) => item.type === 'video' ? 'long' : item.type === 'image' ? 'photo' : item.type;
  const searchedMedia = currentMediaHistory.filter(item => {
    const type = normalizedType(item);
    if (selectedType === 'short' && type !== 'short') return false;
    if (selectedType === 'video' && type !== 'long') return false;
    if (selectedType === 'image' && type !== 'photo') return false;
    const tagMatch = selectedTag === 'all' || (item.tags || []).some(tag => tag.toLowerCase().includes(selectedTag.toLowerCase()));
    const query = searchQuery.trim().toLowerCase();
    const textMatch = !query || `${item.title || ''} ${(item.tags || []).join(' ')}`.toLowerCase().includes(query);
    return tagMatch && textMatch && (isSelf || !isTargetBlocked);
  });
  const filteredHistory = searchedMedia.filter(item => {
    const type = normalizedType(item);
    return contentTab === 'minis' ? type === 'short'
      : contentTab === 'longs' ? type === 'long'
        : contentTab === 'posts' ? type === 'photo' : false;
  });
  const tabCounts = {
    minis: mediaState.counts.minis,
    longs: mediaState.counts.longs,
    posts: mediaState.counts.posts,
    reports: reportState.receivedCount
  };

  if (profileLoading || !blockedUsersReady) return <ProfileSkeleton />;
  if (profileError) return <div role="alert" className="m-4 rounded-xl bg-rose-50 p-4 text-sm text-rose-700">Could not load this profile: {profileError.message}</div>;
  if (blockedUsersError) return <div role="alert" className="m-4 rounded-xl bg-rose-50 p-4 text-sm text-rose-700">Could not load your block list. Profile content is hidden until it is available.</div>;

  return (
    <div id="profile-container" data-screen-scroll-root className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-slate-50">
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain flex flex-col pb-32">
        <div className="mx-auto max-w-5xl w-full">
          <ProfileHeader
            name={displayName}
            username={displayUsername}
            avatar={displayAvatar}
            bio={displayBio}
            location={location}
            email={displayEmail}
            isOfficial={isOfficial}
            isSelf={isSelf}
            isFollowing={isFollowing}
            followingDuration={followingCreatedAt ? formatFollowDuration(followingCreatedAt) : undefined}
            isBlocked={isTargetBlocked}
            followerCount={followerCount}
            followingCount={followingCount}
            views={totalViews}
            likes={totalLikes}
            copied={copiedLink}
            onShare={handleShare}
            onEditAvatar={currentUser && isSelf ? () => fileInputRef.current?.click() : undefined}
            avatarBusy={avatarBusy}
            onSettings={() => setShowSettings(true)}
          onCreators={() => {
            setShowCreators(true);
            void listCreators().then(setCreators).catch(error => setBlockActionError(error instanceof Error ? error.message : 'Could not load creators.'));
          }}
          onFollow={() => void handleProfileFollow()}
          onBlock={() => void toggleTargetBlock()}
          onSubscribers={() => void openFollowList('followers')}
          onFollowing={() => void openFollowList('following')}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          aria-label="Choose a profile photo"
          onChange={event => {
            const file = event.currentTarget.files?.[0];
            if (file) void handleAvatarSelection(file);
          }}
        />
        {avatarError && <p role="alert" className="mx-4 mt-3 rounded-xl bg-rose-50 p-3 text-xs text-rose-700">{avatarError}</p>}

        {blockActionError && <p role="alert" className="mx-4 mt-3 rounded-xl bg-rose-50 p-3 text-xs text-rose-700">{blockActionError}</p>}
        {mediaState.error && <p role="alert" className="mx-4 mt-3 rounded-xl bg-rose-50 p-3 text-xs text-rose-700">Could not load profile uploads: {mediaState.error.message}</p>}

        <div className="px-4">
          {isSelf && blockedUserIds.size > 0 && <section className="my-4 rounded-2xl border border-slate-200 bg-white p-3">
            <button type="button" onClick={() => setShowBlockedUsers(value => !value)} className="flex w-full items-center justify-between text-xs font-bold text-slate-700">
              <span>Blocked accounts ({blockedUserIds.size})</span><span>{showBlockedUsers ? '−' : '+'}</span>
            </button>
            {showBlockedUsers && <div className="mt-3 space-y-2">
              {Array.from(blockedUserIds).map((uid: string) => <div key={uid} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                <span className="truncate text-xs text-slate-600">{uid}</span>
                <button type="button" onClick={() => currentUser && void unblockUser(currentUser.uid, uid).catch(error => setBlockActionError(error instanceof Error ? error.message : 'Could not unblock account.'))} className="ml-2 text-xs font-bold text-blue-800">Unblock</button>
              </div>)}
            </div>}
          </section>}

          <div className="sticky top-0 z-20 bg-white shadow-xs py-2">
            <ProfileTabs
              active={contentTab}
              counts={tabCounts}
              isSelf={isSelf}
              reportPendingCount={reportState.openReceivedCount}
              onChange={setContentTab}
            />
          </div>

          {contentTab === 'reports' && isSelf ? (
            <ReportsTab active data={reportState} />
          ) : (
            <div className="space-y-4 py-4">
              <MediaHistorySearchBar
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                selectedTag={selectedTag}
                onTagSelect={setSelectedTag}
                availableTags={availableTags}
                selectedType={selectedType}
                onTypeSelect={setSelectedType}
                totalCount={mediaState.counts.total}
                filteredCount={filteredHistory.length}
                compact
              />

              {mediaState.loading ? (
                <div className="grid grid-cols-3 gap-2 lg:grid-cols-5">
                  {Array.from({ length: 9 }, (_, index) => <div key={index} className={`animate-pulse rounded-xl bg-slate-200 ${contentTab === 'longs' ? 'aspect-video' : 'aspect-[9/16]'}`} />)}
                </div>
              ) : filteredHistory.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-12 text-center">
                  {contentTab === 'longs' ? <Video className="mx-auto h-8 w-8 text-slate-400" /> : contentTab === 'posts' ? <Image className="mx-auto h-8 w-8 text-slate-400" /> : <Clapperboard className="mx-auto h-8 w-8 text-slate-400" />}
                  <p className="mt-3 text-sm font-bold text-slate-800">No {contentTab === 'minis' ? 'MiniS' : contentTab} uploaded yet.</p>
                  {isSelf && <button type="button" onClick={onOpenUpload} className="mt-4 rounded-xl bg-blue-950 px-4 py-2.5 text-xs font-bold text-white">Upload</button>}
                </div>
              ) : contentTab === 'longs' ? (
                <div className="space-y-3">
                  {filteredHistory.map(item => <article key={item.id} onClick={() => setSelectedMediaItem(item)} className="flex cursor-pointer gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                    <ProfileLongThumbnail item={item} />
                    <div className="min-w-0 flex-1">
                      <h3 className="line-clamp-2 text-sm font-bold text-slate-900">{item.title || ''}</h3>
                      <p className="mt-1 text-[11px] text-slate-500">{formatCount(item.views || 0)} views · {item.uploadDate}</p>
                    </div>
                    <div onClick={event => event.stopPropagation()}><ContentActionsMenu contentId={item.id} contentType="long" contentOwnerUid={item.userId || targetUid} contentTitle={item.title} contentThumbUrl={item.thumbnailUrl} currentUser={currentUser} onOpenAuth={onOpenAuth} onDeleted={onDeleteHistoryItem} /></div>
                  </article>)}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 lg:grid-cols-5">
                  {filteredHistory.map(item => <ProfileMediaTile key={item.id} item={item} isVideo={contentTab === 'minis'} onClick={() => setSelectedMediaItem(item)}>
                    <div className="absolute right-1 top-1 z-10" onClick={event => event.stopPropagation()}>
                      <ContentActionsMenu contentId={item.id} contentType={contentTab === 'minis' ? 'short' : 'photo'} contentOwnerUid={item.userId || targetUid} contentTitle={item.title} contentThumbUrl={item.thumbnailUrl} currentUser={currentUser} onOpenAuth={onOpenAuth} onDeleted={onDeleteHistoryItem} />
                    </div>
                  </ProfileMediaTile>)}
                </div>
              )}

              {mediaState.hasMore && <button type="button" disabled={mediaState.loadingMore} onClick={() => void mediaState.loadMore()} className="w-full rounded-xl border border-slate-200 bg-white py-3 text-xs font-bold text-slate-700 disabled:opacity-50">{mediaState.loadingMore ? 'Loading…' : 'Load more uploads'}</button>}
              {mediaState.loadingMore && <div className="flex justify-center"><LoaderCircle className="h-5 w-5 animate-spin text-blue-900" /></div>}
            </div>
          )}
        </div>
      </div>
    </div>

      {selectedMediaItem && <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/90 p-4">
        <div className="relative flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-3xl border border-slate-800 bg-slate-950">
          <div className="flex items-center justify-between border-b border-slate-800 p-4">
            <div className="flex items-center gap-2"><div className="grid h-8 w-8 place-items-center overflow-hidden rounded-full bg-slate-700">{displayAvatar && <img src={displayAvatar} alt="" className="h-full w-full object-cover" />}</div><span className="text-xs font-bold text-white">@{displayUsername}</span></div>
            <button type="button" aria-label="Close media" onClick={() => { setSelectedMediaItem(null); setShowComments(false); }} className="p-2 text-slate-400"><X className="h-5 w-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto bg-black p-4 text-center">
            {selectedMediaItem.type === 'short' || selectedMediaItem.type === 'minis' || selectedMediaItem.type === 'long' || selectedMediaItem.type === 'video'
              ? <video src={selectedMediaItem.mediaUrl} poster={selectedMediaItem.thumbnailUrl || undefined} controls autoPlay playsInline preload="metadata" className="mx-auto max-h-[55vh] w-full rounded-2xl object-contain" />
              : <img src={selectedMediaItem.mediaUrl || selectedMediaItem.thumbnailUrl} alt={selectedMediaItem.title || ''} className="mx-auto max-h-[55vh] w-full rounded-2xl object-contain" />}
            <p className="mt-3 text-sm text-white">{selectedMediaItem.title || ''}</p>
            {showComments && <div className="mt-4 rounded-2xl bg-white p-3 text-left">
              <PostCommentsSection postId={selectedMediaItem.id} currentUser={currentUser} onOpenAuth={() => onOpenAuth?.()} onCommentCountChange={() => undefined} blockedUserIds={blockedUserIds} onOpenProfile={onOpenProfile} />
            </div>}
          </div>
          <div className="flex items-center justify-around border-t border-slate-800 bg-slate-900 p-3 text-white">
            <button type="button" disabled={likeBusy} aria-pressed={selectedLiked} onClick={() => void handleMediaLike()} className="flex min-h-10 items-center gap-1.5 text-xs font-bold"><Heart className={`h-5 w-5 ${selectedLiked ? 'fill-rose-500 text-rose-500' : ''}`} />{formatCount(selectedMediaItem.likes || 0)}</button>
            <button type="button" aria-expanded={showComments} onClick={() => setShowComments(value => !value)} className="flex min-h-10 items-center gap-1.5 text-xs font-bold"><MessageCircle className="h-5 w-5" />Comment</button>
            <button type="button" onClick={() => void handleRepost(selectedMediaItem)} className="flex min-h-10 items-center gap-1.5 text-xs font-bold"><Repeat className="h-5 w-5" />Repost</button>
            <button type="button" aria-pressed={isSavedActive} onClick={() => setIsSavedActive(value => !value)} className="flex min-h-10 items-center gap-1.5 text-xs font-bold"><Bookmark className={`h-5 w-5 ${isSavedActive ? 'fill-amber-400 text-amber-400' : ''}`} />Save</button>
          </div>
        </div>
      </div>}

      {showCreators && <div className="fixed inset-0 z-[140] flex items-center justify-center bg-slate-950/60 p-4">
        <div className="max-h-[85vh] w-full max-w-sm overflow-y-auto rounded-3xl bg-white p-4">
          <div className="mb-3 flex items-center justify-between"><h3 className="text-sm font-bold">Creators</h3><button type="button" onClick={() => setShowCreators(false)} className="rounded-full p-2 text-slate-400"><X className="h-4 w-4" /></button></div>
          {creators.map(creator => <div key={creator.uid} className="flex items-center gap-3 border-b border-slate-100 py-3">
            <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => { setShowCreators(false); onOpenProfile?.(creator.uid); }}>
              <div className="grid h-9 w-9 place-items-center overflow-hidden rounded-full bg-slate-100">{creator.photoURL && <img src={creator.photoURL} alt="" className="h-full w-full object-cover" />}</div>
              <span className="min-w-0"><strong className="block truncate text-xs">{creator.displayName}</strong><span className="block truncate text-[10px] text-blue-700">@{creator.username || ''}</span></span>
            </button>
            <FollowButton
              targetUid={creator.uid}
              targetName={creator.displayName || creator.username || 'creator'}
              currentUser={currentUser}
              onOpenAuth={onOpenAuth}
              compact
            />
          </div>)}
          {creators.length === 0 && <p className="py-8 text-center text-xs text-slate-500">No public creators with published content yet.</p>}
        </div>
      </div>}

      {showSettings && <div className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-950/60 p-4">
        <div className="max-h-[85vh] w-full max-w-sm overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4"><h3 className="text-sm font-bold">Account settings</h3><button type="button" onClick={() => setShowSettings(false)} className="rounded-full bg-slate-100 p-2 text-slate-500"><X className="h-4 w-4" /></button></div>
          {(onOpenFeedback || onOpenAppSupport) && <div className="grid grid-cols-2 gap-2 border-b border-slate-100 py-3">
            {onOpenFeedback && <button type="button" onClick={onOpenFeedback} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50">Feedback</button>}
            {onOpenAppSupport && <button type="button" onClick={onOpenAppSupport} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50">App Support</button>}
          </div>}
          <form onSubmit={event => void handleSaveSettings(event)} className="space-y-4 pt-4">
            <div className="flex flex-col items-center gap-3">
              <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-3xl bg-slate-100">{avatar && <img src={avatar} alt="" className="h-full w-full object-cover" />}</div>
              <button type="button" disabled={avatarBusy} onClick={() => fileInputRef.current?.click()} className="text-xs font-bold text-blue-900 disabled:opacity-50">{avatarBusy ? 'Saving profile photo…' : 'Change profile photo'}</button>
              {avatarError && <p role="alert" className="text-center text-xs text-rose-700">{avatarError}</p>}
            </div>
            {[
              { label: 'Full name', value: name, set: setName, editable: true },
              { label: 'Username', value: username, set: setUsername, editable: true },
              { label: 'Email', value: email, set: setEmail, editable: false },
              { label: 'Phone', value: phone, set: setPhone, editable: false }
            ].map(field => <label key={field.label} className="block text-xs font-semibold text-slate-600">{field.label}
              <input type="text" value={field.value} disabled={!field.editable} onChange={event => field.editable && field.set(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm disabled:text-slate-400" />
            </label>)}
            {usernameError && <p role="alert" className="text-xs text-rose-700">{usernameError}</p>}
            {settingsSavedToast && <p role="status" className="flex items-center gap-2 text-xs text-emerald-700"><Check className="h-4 w-4" />Saved</p>}
            <button type="submit" disabled={isSavingSettings} className="w-full rounded-xl bg-blue-950 py-3 text-xs font-bold text-white disabled:opacity-50">{isSavingSettings ? 'Saving…' : 'Save changes'}</button>
            <button type="button" onClick={onLogout} className="w-full rounded-xl border border-rose-200 py-3 text-xs font-bold text-rose-700">Log out</button>
            <button
              type="button"
              onClick={async () => {
                if (window.confirm('Are you sure you want to permanently delete your YeTryKar account? This action cannot be undone.')) {
                  try {
                    if (auth.currentUser) {
                      await deleteUser(auth.currentUser);
                    }
                    alert('Your account deleted successfully.');
                    if (onLogout) onLogout();
                  } catch (err: any) {
                    alert('Failed to delete account: ' + (err?.message || 'Please sign in again before deleting your account.'));
                  }
                }
              }}
              className="w-full rounded-xl bg-rose-600 hover:bg-rose-700 py-3 text-xs font-bold text-white mt-2 cursor-pointer"
            >
              Delete account
            </button>
          </form>
        </div>
      </div>}

      {showSubscribersModal && <div className="fixed inset-0 z-[160] flex items-center justify-center bg-slate-950/60 p-4">
        <div className="max-h-[85vh] w-full max-w-sm space-y-3 overflow-y-auto rounded-3xl bg-white p-5">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3"><h3 className="flex items-center gap-2 text-sm font-bold"><Users className="h-4 w-4 text-blue-900" />{followListType === 'followers' ? `Followers (${followerCount})` : `Following (${followingCount})`}</h3><button type="button" onClick={() => setShowSubscribersModal(false)} className="p-2 text-slate-400"><X className="h-4 w-4" /></button></div>
          {loadingSubscribers && subscribersList.length === 0 ? <p className="py-6 text-center text-xs text-slate-500">Loading {followListType}…</p> : subscribersList.length ? subscribersList.map(sub => <div key={sub.id} className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
            <div><p className="text-xs font-bold text-slate-900">{sub.fanName}</p>{sub.fanUsername && <p className="text-[10px] text-blue-700">@{sub.fanUsername}</p>}<p className="mt-1 text-[10px] text-slate-500">Following since {formatFollowDuration(sub.createdAt)}</p></div>
            {onOpenProfile && <button type="button" onClick={() => { setShowSubscribersModal(false); onOpenProfile(sub.fanUid); }} className="text-[10px] font-bold text-blue-900">Profile</button>}
          </div>) : !loadingSubscribers ? <p className="py-8 text-center text-xs text-slate-500">No {followListType} yet.</p> : null}
          {followListError && <p role="alert" className="rounded-lg bg-rose-50 p-2 text-xs text-rose-700">{followListError}</p>}
          {hasMoreFollowList && <button type="button" disabled={loadingSubscribers} onClick={() => void loadMoreFollowList()} className="w-full rounded-xl border border-slate-200 py-2 text-xs font-bold text-blue-900 disabled:opacity-50">{loadingSubscribers ? 'Loading…' : 'Load more'}</button>}
        </div>
      </div>}

      {settingsSavedToast && !showSettings && <div className="fixed bottom-5 left-1/2 z-[170] -translate-x-1/2 rounded-xl bg-emerald-700 px-4 py-2 text-xs font-bold text-white">Profile saved</div>}
    </div>
  );
}
